import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, StateModel } from "@insite/client-framework/Types/ApiModels";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select/Select";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { setCookie } from "@insite/mobius/utilities/cookies";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import breakpoint from "@store/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@store/Framework/Common/Utilities/unitHelper";
import { useCurrentPage } from "@store/Framework/Store/Data/Pages/PageHooks";
import EditFill from "@store/Library/Components/Icons/Edit/EditFill";
import PinFill from "@store/Library/Components/Icons/Pin/PinFill";
import Search from "@store/Library/Components/Icons/Search";
import Button from "@store/MobiusOverrides/Button/Button";
import { iconScalableStyles } from "@store/Themes/common/mixins/icon";
import { spacingStyles } from "@store/Themes/common/mixins/spacing";
import { menuPanelProps } from "@store/Themes/common/props/menu";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    context: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const countries: CountryModel[] | undefined = getCurrentCountries(state);

    return {
        isAuthenticated: session && (session.isAuthenticated || session.rememberMe) && !session.isGuest,
        isGuest: session.isGuest,
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        locationFinderUrl: getPageLinkByPageType(state, "LocationFinderPage")?.url,
        shipTo: getShipToState(state, session.shipToId).value,
        countries,
        useOneTimeAddress: state.pages.checkoutShipping.useOneTimeAddress,
    };
};

const mapDispatchToProps = {
    loadCurrentCountries,
    signOut,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

export interface LocatorDisplayStyles {
    locator?: InjectableCss;
    signInButton?: ButtonPresentationProps;
    updateLocationButton?: ButtonPresentationProps;
    branchLocatorButton?: ButtonPresentationProps;
    locatorContainer?: GridContainerProps;
    locatorItem?: GridItemProps;
    locatorPopupContainer?: InjectableCss;
    locatorPopupRow?: InjectableCss;
    locatorPopupButtonsRow?: InjectableCss;
    locatorPopupLink?: LinkPresentationProps;
    locatorPopupState?: TypographyProps;
    locatorSignInButton?: ButtonPresentationProps;
    locatorCloseButton?: ButtonPresentationProps;
    locationUpdateButton?: ButtonPresentationProps;
    headerChange?: LinkPresentationProps;
    mobileHeaderChange?: LinkPresentationProps;
    panelTitle?: TypographyProps;
    select?: SelectPresentationProps;
    iconPin?: IconPresentationProps;
    iconEdit?: IconPresentationProps;
    iconSearch?: IconPresentationProps;
    locatorDisplay?: InjectableCss;
    locatorCurrent?: TypographyProps;
    locatorChange?: LinkPresentationProps;
    drawer?: DrawerPresentationProps;
}

const locatorInlineButtonStyles = css`
    ${x => x.theme.button.primary.css}
    height: ${pxToRem(40)};
    background-color: ${getColor("primary.contrast")};
`;

const locatorButtonStyles = css`
    ${spacingStyles(pxToRem(20), null)}
    width:100%;
`;

const iconStyles = (iconSrc: React.ComponentType<{}>) => {
    const iconProps: IconPresentationProps = {
        src: iconSrc,
        color: "inherit",
        css: css`
            margin-right: ${pxToRem(4)};
        `,
    };
    return iconProps;
};

const changeIcon = css`
    > span {
        display: inline-flex;
        align-items: center;
        flex-direction: row;
        > ${IconWrapper} {
            ${iconScalableStyles("1.5em")}
        }
    }
`;

export const locatorDisplayStyles: LocatorDisplayStyles = {
    signInButton: {
        variant: "primary",
        css: css`
            ${x => x.theme.button.primary.css}
            ${locatorButtonStyles}
        `,
    },
    updateLocationButton: {
        variant: "secondary",
        css: css`
            ${x => x.theme.button.secondary.css}
            ${locatorButtonStyles}
        `,
    },
    branchLocatorButton: {
        variant: "primary",
        css: css`
            ${x => x.theme.button.primary.css}
            ${locatorButtonStyles}
        `,
    },
    locationUpdateButton: {
        variant: "secondary",
        css: css`
            ${x => x.theme.button.secondary.css}
            ${locatorButtonStyles}
        `,
    },
    locatorCloseButton: {
        variant: "primary",
        buttonType: "outline",
        css: css`
            ${locatorInlineButtonStyles}
            width: 30%;
        `,
    },
    locatorSignInButton: {
        variant: "primary",
        buttonType: "outline",
        css: css`
            ${locatorInlineButtonStyles}
            width: 60%;
        `,
    },
    locatorContainer: {
        gap: 20,
        css: css`
            max-width: ${pxToRem(400)};
            color: ${getColor("text")};
        `,
    },
    locatorItem: {
        width: 12,
        css: css`
            flex-direction: column;
            ${spacingStyles(pxToRem(40), null)}
        `,
    },
    locatorPopupContainer: {
        css: css`
            width: 100%;
            height: ${pxToRem(150)};
            max-width: ${pxToRem(330)};
            padding: ${pxToRem(20)};
            background-color: ${getColor("primary.light")};
            z-index: ${getProp("theme.zIndex.modal")};
            touch-action: none;
            position: absolute;
            inset: 0;
            text-align: left;
            top: ${pxToRem(37)};
            &::before {
                display: block;
                content: "";
                width: ${pxToRem(20)};
                height: ${pxToRem(20)};
                z-index: 1301;
                position: absolute;
                left: 50%;
                top: ${pxToRem(-6)};
                margin-left: ${pxToRem(-10)};
                transform: rotate(45deg);
                background-color: ${getColor("primary.light")};
            }
        `,
    },
    locatorPopupRow: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding-top: ${pxToRem(10)};
        `,
    },
    locatorPopupButtonsRow: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding-top: ${pxToRem(15)};
        `,
    },
    locatorPopupState: {
        weight: "bold",
        css: css`
            font-size: ${x => x.theme.design.typography.size.base};
        `,
    },
    locatorPopupLink: {
        css: css`
            color: ${getColor("primary.contrast")};
            text-decoration: underline;
        `,
    },
    headerChange: {
        css: css`
            color: ${getColor("primary.contrast")};
            margin-right: ${pxToRem(30)};
            margin-top: ${pxToRem(5)};
            ${changeIcon}
        `,
    },
    mobileHeaderChange: {
        css: css`
            color: ${getColor("primary")};
            margin: ${pxToRem(5)};
            ${changeIcon}
            ${breakpoint("md", "up")} {
                display: none;
            }
        `,
    },
    panelTitle: {
        css: css`
            margin-bottom: 0;
        `,
    },
    locatorDisplay: {
        css: css`
            display: flex;
            justify-content: space-between;
        `,
    },
    locatorCurrent: {
        css: css`
            display: inline-flex;
            align-items: center;
            flex-direction: row;
            height: ${pxToRem(60)};
        `,
    },
    iconEdit: iconStyles(EditFill),
    iconPin: iconStyles(PinFill),
    iconSearch: iconStyles(Search),
    drawer: {
        cssOverrides: {
            drawerContainer: css``,
            drawerContent: css`
                padding: 20px;
                overflow-x: hidden;
            `,
        },
        closeButtonProps: menuPanelProps.closeButton,
        closeButtonIconProps: menuPanelProps.closeButtonIcon,
    },
};

const styles = locatorDisplayStyles;

const LocatorDisplay = ({
    context,
    isAuthenticated,
    isGuest,
    signInUrl,
    locationFinderUrl,
    signOut,
    history,
    countries,
    loadCurrentCountries,
    shipTo,
}: Props) => {
    const [locatorPopup, setLocatorPopup] = useState(false);
    const [countriesLoaded, setCountriesLoaded] = useState(false);
    const [selectedCountry, setSelectedCountry] = React.useState<CountryModel | undefined>(undefined);
    const [availableStates, setAvailableStates] = React.useState<StateModel[]>([]);
    const [shipState, setShipState] = useState<StateModel>();
    const [isOpen, setIsOpen] = useState(false);
    const togglePanel = () => setIsOpen(!isOpen);
    const isGuestOrNotAuthenticated = !isAuthenticated || isGuest;
    const isAuthenticatedAndNotGuest = isAuthenticated && !isGuest;
    const currentPage = useCurrentPage();
    const isProductDetailsPage = currentPage.type === constants.pageType.productDetails;
    const isProductListPage = currentPage.type === constants.pageType.productList;

    useEffect(() => {
        if (!countries) {
            loadCurrentCountries();
        } else {
            setCountriesLoaded(true);
        }
    }, [countries]);

    useEffect(() => {
        if (!shipState && countries) {
            const locationCookie = getCookie("Location");
            const locationCountryCookie = getCookie("LocationCountry");
            let shipToCountry = "";
            let shipToState = "";
            if (
                isGuestOrNotAuthenticated &&
                typeof locationCookie !== "undefined" &&
                typeof locationCountryCookie !== "undefined"
            ) {
                // If not logged in and cookies are set
                shipToCountry = locationCountryCookie;
                shipToState = locationCookie;
            } else if (shipTo?.country?.abbreviation && shipTo?.state?.abbreviation) {
                // If user logged in, set shipState to customer shipTo
                shipToCountry = shipTo.country.abbreviation;
                shipToState = shipTo.state.abbreviation;
            } else if (typeof locationCookie !== "undefined" && typeof locationCountryCookie !== "undefined") {
                // If cookies are set and customer has no shipTo, set shipState to these
                shipToCountry = locationCountryCookie;
                shipToState = locationCookie;
            } else {
                // If not logged in and no cookies are set, load default
                shipToCountry = "CA";
                shipToState = "QC";
            }
            shipToSetup(shipToCountry, shipToState);
        }
    }, [shipState, shipTo, countries]);

    useEffect(() => {
        if (selectedCountry) {
            setAvailableStates(selectedCountry?.states || []);
        }
    }, [selectedCountry]);

    const shipToSetup = (countryCode: string, stateCode: string) => {
        const selectedCountry = countries?.find((c: { abbreviation: string }) => c.abbreviation === countryCode);
        if (selectedCountry) {
            const selectedState = selectedCountry.states?.find(
                (c: { abbreviation: string }) => c.abbreviation === stateCode,
            );
            setCookie("LocationCountry", countryCode);
            setCookie("Location", stateCode);
            setShipState(selectedState);
            setSelectedCountry(selectedCountry);
        }
    };

    const openPanel = () => {
        togglePanel();
        closePopup();
    };

    const closePopup = () => {
        setLocatorPopup(false);
        setCookie("locatorPopup", "unloaded");
    };

    const signInAction = () => {
        closePopup();
        history.push(`${signInUrl}`);
    };

    const locatorPageAction = () => {
        togglePanel();
        history.push(`${locationFinderUrl}`);
    };

    const handleSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        if (selectedCountry) {
            const selectedState = selectedCountry.states?.find(c => c.id === event.currentTarget.value);
            setShipState(selectedState);
        }
    };

    const locationUpdateAction = () => {
        if (shipState) {
            setCookie("Location", shipState.abbreviation);
            // Wait for set cookie
            setTimeout(() => {
                window.location.reload();
            }, 250);
        }
    };

    const locatorChange: LinkPresentationProps = {
        css: css`
            margin-top: ${context === "widget" ? pxToRem(0) : pxToRem(30)};
            margin-left: ${pxToRem(4)};
            > span {
                display: inline-flex;
                align-items: center;
                flex-direction: row;
                height: ${context === "widget" ? pxToRem(60) : pxToRem(45)};
            }
        `,
    };

    const locatorPopupStyles: InjectableCss = {
        css: css`
            ${styles.locatorPopupContainer?.css}
            animation: fadeIn 250ms ease;

            /* stylelint-disable-next-line keyframes-name-pattern */
            @keyframes fadeIn {
                0% {
                    opacity: 0;
                }
                100% {
                    opacity: 1;
                }
            }
        `,
    };

    setTimeout(() => {
        if (getCookie("locatorPopup") !== "unloaded" && (isProductDetailsPage || isProductListPage)) {
            setLocatorPopup(true);
        }
    }, 1000);

    if (!shipState) {
        return null;
    }

    const HeaderLocator = () => {
        if (context === "widget" || context === "AddressForm") {
            return null;
        }

        const headerChangeStyle = context === "mobile" ? styles.mobileHeaderChange : styles.headerChange;

        return (
            <>
                <Link onClick={openPanel} {...headerChangeStyle}>
                    <Icon {...styles.iconPin} /> {context !== "mobile" && translate("Locator")}
                </Link>
                {locatorPopup && context === "header" && (
                    <StyledWrapper {...locatorPopupStyles}>
                        <Typography>{translate("Default shipping location is set to:")}</Typography>
                        <StyledWrapper {...styles.locatorPopupRow}>
                            {shipState && <Typography {...styles.locatorPopupState}>{shipState.name}</Typography>}
                            {isGuestOrNotAuthenticated && (
                                <Typography>
                                    (
                                    <Link onClick={openPanel} {...styles.locatorPopupLink}>
                                        {` ${translate("Change")} `}
                                    </Link>
                                    )
                                </Typography>
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.locatorPopupButtonsRow}>
                            <Button {...styles.locatorCloseButton} onClick={closePopup}>
                                {translate("Ok")}
                            </Button>
                            <Button {...styles.locatorSignInButton} onClick={signInAction}>
                                {translate("Sign In")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </>
        );
    };

    const LocatorDisplayWidget = () => (
        <StyledWrapper {...styles.locatorDisplay}>
            {context === "widget" && (
                <Typography {...styles.locatorCurrent}>
                    <Icon {...styles.iconPin} /> {translate("Your shipping location:")}{" "}
                    {shipState ? shipState.name : ""}
                </Typography>
            )}
            <Link onClick={openPanel} {...locatorChange}>
                <Icon {...styles.iconEdit} /> {translate("Change")}
            </Link>
        </StyledWrapper>
    );

    return (
        <StyledWrapper {...styles.locator}>
            <HeaderLocator />
            {isGuestOrNotAuthenticated && (context === "widget" || context === "AddressForm") && (
                <LocatorDisplayWidget />
            )}
            <Drawer
                className="drawer"
                position="right"
                {...styles.drawer}
                isOpen={isOpen}
                handleClose={togglePanel}
                contentLabel="menu drawer"
            >
                <GridContainer {...styles.locatorContainer}>
                    {isGuestOrNotAuthenticated && (
                        <GridItem {...styles.locatorItem}>
                            <Typography variant="h2" {...styles.panelTitle}>
                                {translate("Have an SPI account ?")}
                            </Typography>
                            {translate("Sign in to see your prices and availability that match your account.")}
                            <Button {...styles.signInButton} onClick={signInAction}>
                                {translate("Sign In")}
                            </Button>
                        </GridItem>
                    )}
                    <GridItem {...styles.locatorItem}>
                        <Typography variant="h2" {...styles.panelTitle}>
                            <Icon {...styles.iconPin} /> {translate("Location")}
                        </Typography>
                        {isGuestOrNotAuthenticated && (
                            <Typography as="p" variant="p">
                                {translate("Update your shipping location to get accurate prices and availability")}
                            </Typography>
                        )}
                        {isAuthenticatedAndNotGuest && (
                            <>
                                <Typography as="p" variant="p">
                                    {translate("Current location linked to your account")}
                                </Typography>
                                <Typography as="p" variant="p">
                                    {translate(
                                        "this is the location linked to your SPI customer account. When you are logged in you get all the correct price, availability and shipping cost for your account agreement.",
                                    )}
                                </Typography>
                                <Typography as="p" variant="p">
                                    {translate(
                                        "Please contact customer service at 1 855 774-7227 for more information.",
                                    )}
                                </Typography>
                            </>
                        )}
                        {availableStates && (
                            <Select
                                {...styles.select}
                                onChange={handleSelectChange}
                                value={shipState ? shipState.id : ""}
                                disabled={!!isAuthenticatedAndNotGuest}
                            >
                                <option value="">{translate("Select your Ship to")}</option>
                                {availableStates.map((state, key) => (
                                    <option key={key} value={state.id}>
                                        {state.name}
                                    </option>
                                ))}
                            </Select>
                        )}
                        {isGuestOrNotAuthenticated && (
                            <Button {...styles.locationUpdateButton} onClick={locationUpdateAction}>
                                {translate("Update Location")}
                            </Button>
                        )}
                        {isAuthenticatedAndNotGuest && (
                            <Typography as="p" variant="p">
                                {`${translate("Not your account ?")} `}
                                <Link
                                    onClick={(event: React.MouseEvent) => {
                                        signOut();
                                    }}
                                >
                                    {translate("Sign Out")}
                                </Link>
                            </Typography>
                        )}
                    </GridItem>
                    <GridItem {...styles.locatorItem}>
                        <Typography variant="h2" {...styles.panelTitle}>
                            <Icon {...styles.iconSearch} /> {translate("Location Finder")}
                        </Typography>
                        <Button {...styles.branchLocatorButton} onClick={locatorPageAction}>
                            {translate("Branch Locator")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Drawer>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withHistory(LocatorDisplay));
